import { useEffect, useRef, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { api, fileUrl, formatDMY } from "@/lib/api";
import { toast } from "sonner";
import { generateReceiptPdf } from "@/lib/receipt";
import {
  Plus,
  User,
  Home,
  Phone,
  Calendar,
  Wallet,
  Camera,
  Clock,
  RefreshCw,
  CheckCircle2,
  Paperclip,
  X,
  FileVideo,
  Image as ImageIcon,
  Download,
  FileText,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["Maintenance", "Food", "Wi-Fi", "Cleanliness", "Other"];

export default function StudentDashboard() {
  const [profile, setProfile] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [category, setCategory] = useState("Maintenance");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInput = useRef(null);
  const attachmentInput = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/students/me/self");
      setProfile(data);
    } catch {}
    try {
      const { data } = await api.get("/complaints");
      setComplaints(data);
    } catch {}
    try {
      const { data } = await api.get("/receipts/mine");
      setReceipts(data);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/complaints", {
        category,
        description,
        attachments: attachments.map((a) => a.path),
      });
      toast.success("Complaint submitted");
      setDescription("");
      setAttachments([]);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const handleAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingAttachment(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [
          ...prev,
          { path: data.path, name: file.name, type: file.type },
        ]);
      }
      toast.success("Attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Attachment upload failed");
    } finally {
      setUploadingAttachment(false);
      if (attachmentInput.current) attachmentInput.current.value = "";
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/students/me/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPhoto(false);
      if (photoInput.current) photoInput.current.value = "";
    }
  };

  const statusBadge = (s) => {
    if (s === "Resolved") return "bg-[#00CC66] text-white";
    if (s === "In Progress") return "bg-[#FF9900] text-black";
    return "bg-[#FF4D4D] text-white";
  };

  // Complaint counts
  const counts = {
    total: complaints.length,
    pending: complaints.filter((c) => c.status === "Pending").length,
    in_progress: complaints.filter((c) => c.status === "In Progress").length,
    resolved: complaints.filter((c) => c.status === "Resolved").length,
  };

  return (
    <DashboardShell
      title="Your dashboard"
      subtitle="Vasavi Boys Hostel"
      testId="student-dashboard"
    >
      {!profile ? (
        <div className="brutal-card p-8 bg-[#FFE600]">
          <h3 className="font-heading font-black text-2xl">Profile not linked yet</h3>
          <p className="mt-2 font-medium">
            Your enrolment record isn't linked to this account yet. Please ask the admin to add your
            details. You can still submit complaints below.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="brutal-card p-6 bg-white lg:col-span-1">
              <div className="relative">
                {profile.photo_path ? (
                  <img
                    src={fileUrl(profile.photo_path)}
                    alt={profile.full_name}
                    className="w-full h-56 object-cover border-2 border-[#0A0A0A] rounded-xl"
                    data-testid="student-photo"
                  />
                ) : (
                  <div
                    className="w-full h-56 flex items-center justify-center border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600]"
                    data-testid="student-photo-placeholder"
                  >
                    <User size={72} strokeWidth={2} />
                  </div>
                )}
                <label
                  className="absolute bottom-3 right-3 inline-flex items-center gap-2 font-bold text-xs px-3 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform cursor-pointer"
                  data-testid="student-photo-upload-label"
                >
                  <Camera size={14} strokeWidth={2.5} />
                  {uploadingPhoto ? "Uploading..." : profile.photo_path ? "Change" : "Upload"}
                  <input
                    ref={photoInput}
                    type="file"
                    accept="image/*"
                    onChange={handlePhoto}
                    className="hidden"
                    data-testid="student-photo-upload-input"
                  />
                </label>
              </div>
              <h2 className="font-heading font-black text-2xl mt-4">{profile.full_name}</h2>
              <div className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">
                {profile.student_id_number}
              </div>
            </div>
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Tile icon={Home} label="Flat / Room" value={profile.flat_number} />
              <Tile icon={Calendar} label="Joined" value={formatDMY(profile.date_of_joining)} />
              <Tile icon={Phone} label="Contact" value={profile.contact_number} />
              <Tile icon={Phone} label="Parent Contact" value={profile.parent_contact_number} />
              {profile.year && <Tile icon={Home} label="Year" value={profile.year} />}
              {profile.branch && <Tile icon={Home} label="Branch" value={profile.branch} />}
              {profile.blood_group && <Tile icon={Home} label="Blood Group" value={profile.blood_group} />}
              {profile.food_preference && <Tile icon={Home} label="Food Preference" value={profile.food_preference} />}
            </div>
          </div>

          {/* Fees summary — bold color-coded strip */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="fees-summary">
            <StatTile
              icon={Wallet}
              label="Total Fees"
              value={`₹${Number(profile.total_fees).toLocaleString()}`}
              bg="#FFE600"
              fg="#0A0A0A"
              testId="fees-total"
            />
            <StatTile
              icon={CheckCircle2}
              label="Fees Paid"
              value={`₹${Number(profile.fees_paid).toLocaleString()}`}
              bg="#00CC66"
              fg="#FFFFFF"
              testId="fees-paid"
            />
            <StatTile
              icon={Clock}
              label={profile.dues <= 0 ? "Cleared" : "Fees Pending"}
              value={
                profile.dues <= 0
                  ? "All settled"
                  : `₹${Number(profile.dues).toLocaleString()}`
              }
              bg={profile.dues <= 0 ? "#0A0A0A" : "#FF4D4D"}
              fg={profile.dues <= 0 ? "#FFE600" : "#FFFFFF"}
              testId="fees-pending"
            />
          </div>

          {/* Complaint counts */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="complaint-summary">
            <StatTile
              icon={RefreshCw}
              label="Total Raised"
              value={counts.total}
              bg="#FFFFFF"
              fg="#0A0A0A"
              testId="complaints-total"
            />
            <StatTile
              icon={Clock}
              label="Pending"
              value={counts.pending}
              bg="#FF4D4D"
              fg="#FFFFFF"
              testId="complaints-pending"
            />
            <StatTile
              icon={RefreshCw}
              label="In Progress"
              value={counts.in_progress}
              bg="#FF9900"
              fg="#0A0A0A"
              testId="complaints-in-progress"
            />
            <StatTile
              icon={CheckCircle2}
              label="Resolved"
              value={counts.resolved}
              bg="#00CC66"
              fg="#FFFFFF"
              testId="complaints-resolved"
            />
          </div>
        </>
      )}

      {/* Receipts */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-black text-3xl tracking-tighter">
            Fee receipts
          </h2>
          <span className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">
            {receipts.length} {receipts.length === 1 ? "receipt" : "receipts"}
          </span>
        </div>
        {receipts.length === 0 ? (
          <div className="brutal-card p-6 bg-white text-center font-medium text-[#4A4A4A]" data-testid="no-receipts">
            No receipts yet. Once admin records a payment against your account, a downloadable receipt shows up here.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="student-receipts">
            {receipts.map((r) => (
              <div
                key={r.id}
                className="brutal-card p-5 bg-white flex items-start justify-between gap-4"
                data-testid={`student-receipt-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <FileText size={16} strokeWidth={2.5} />
                    <span className="font-mono text-xs font-bold">{r.receipt_number}</span>
                  </div>
                  <div className="font-heading font-black text-2xl tracking-tighter mt-1">
                    ₹{Number(r.amount).toLocaleString("en-IN")}
                  </div>
                  <div className="text-xs font-medium text-[#4A4A4A] mt-1">
                    {new Date(r.payment_date || r.issued_at).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}{" "}
                    · {r.payment_method || "Cash"}
                  </div>
                  <div className="mt-2">
                    <span
                      className={`inline-block px-2.5 py-0.5 border-2 border-[#0A0A0A] rounded-full text-[11px] font-bold ${
                        Number(r.balance_due) <= 0
                          ? "bg-[#00CC66] text-white"
                          : "bg-[#FF4D4D] text-white"
                      }`}
                    >
                      Balance ₹{Number(r.balance_due).toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => generateReceiptPdf(r)}
                  data-testid={`download-receipt-${r.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 font-bold text-xs border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform whitespace-nowrap"
                >
                  <Download size={13} strokeWidth={2.5} /> PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Complaints */}
      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-heading font-black text-3xl tracking-tighter">Your complaints</h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="new-complaint-button"
          className="inline-flex items-center gap-2 font-bold px-5 py-2.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
        >
          <Plus size={16} /> {showForm ? "Close" : "Raise Complaint"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          className="brutal-card p-6 bg-white mt-4"
          data-testid="complaint-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                Category
              </label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  data-testid="complaint-category"
                  className="border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-2 border-[#0A0A0A]">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                Description
              </label>
              <textarea
                data-testid="complaint-description"
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:ring-offset-2"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={saving || !profile}
            data-testid="submit-complaint"
            className="mt-4 inline-flex items-center gap-2 font-bold px-5 py-2.5 border-2 border-[#0A0A0A] rounded-full bg-[#0A0A0A] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
          >
            {saving ? "Submitting..." : "Submit"}
          </button>
          {/* Attachments */}
          <div className="mt-4 pt-4 border-t-2 border-[#0A0A0A]" data-testid="complaint-attachments">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                <Paperclip size={14} strokeWidth={2.5} /> Attachments (photo / video)
              </div>
              <label className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] hover:shadow-brutal-sm transition-shadow cursor-pointer">
                <Paperclip size={14} strokeWidth={2.5} />
                {uploadingAttachment ? "Uploading..." : "Attach"}
                <input
                  ref={attachmentInput}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleAttachment}
                  className="hidden"
                  data-testid="complaint-attachment-input"
                />
              </label>
            </div>
            {attachments.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {attachments.map((a, i) => {
                  const isVideo = (a.type || "").startsWith("video/");
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-3 py-1.5 border-2 border-[#0A0A0A] rounded-full bg-white text-xs font-bold"
                      data-testid={`attachment-chip-${i}`}
                    >
                      {isVideo ? <FileVideo size={14} /> : <ImageIcon size={14} />}
                      <span className="max-w-[160px] truncate">{a.name}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(i)}
                        className="ml-1 p-0.5 rounded-full bg-[#FF4D4D] text-white"
                        aria-label="Remove attachment"
                      >
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {!profile && (
            <p className="text-xs font-bold text-[#FF4D4D] mt-2">
              Ask admin to link your enrolment before raising a complaint.
            </p>
          )}
        </form>
      )}

      <div className="mt-4 space-y-3" data-testid="my-complaints-list">
        {complaints.length === 0 ? (
          <div className="brutal-card p-6 bg-white text-center font-medium text-[#4A4A4A]">
            You haven't raised any complaints yet.
          </div>
        ) : (
          complaints.map((c) => (
            <div key={c.id} className="brutal-card p-5 bg-white">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600]">
                  {c.category}
                </span>
                <span
                  className={`text-xs font-bold uppercase tracking-widest px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full ${statusBadge(
                    c.status
                  )}`}
                >
                  {c.status}
                </span>
                <span className="text-xs font-medium text-[#4A4A4A]">{formatDMY(c.date_of_complaint)}</span>
              </div>
              <p className="mt-2 font-medium">{c.description}</p>
              {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`complaint-attachments-${c.id}`}>
                  {c.attachments.map((path, i) => {
                    const isVideo = /\.(mp4|mov|webm)$/i.test(path);
                    return (
                      <a
                        key={i}
                        href={fileUrl(path)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1 border-2 border-[#0A0A0A] rounded-full bg-[#FFFCE0] text-xs font-bold hover:shadow-brutal-sm transition-shadow"
                      >
                        {isVideo ? <FileVideo size={12} /> : <ImageIcon size={12} />}
                        {isVideo ? "Video" : "Photo"} #{i + 1}
                      </a>
                    );
                  })}
                </div>
              )}
              {c.admin_remarks && (
                <div className="mt-3 bg-[#F8F9FA] border-2 border-[#0A0A0A] rounded-xl p-3">
                  <div className="text-xs font-bold uppercase tracking-widest">Admin response</div>
                  <div className="font-medium">{c.admin_remarks}</div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </DashboardShell>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="brutal-card p-5 bg-white">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
        <Icon size={14} strokeWidth={2.5} /> {label}
      </div>
      <div className="font-heading font-bold text-lg mt-1">{value || "—"}</div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, bg, fg, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        backgroundColor: bg,
        color: fg,
        border: "2px solid #0A0A0A",
        borderRadius: "0.75rem",
        boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)",
        padding: "1.25rem",
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em]">{label}</span>
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div className="font-heading font-black text-3xl tracking-tighter mt-2">{value}</div>
    </div>
  );
}
